//src/components/BotSettings/TradingParameters.tsx
// BLOKAS 1 TradingParameters.tsx: IMPORTAI
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Settings, Info, AlertCircle, TrendingUp, Target } from 'lucide-react';
import { cn } from '../../utils/cn';
import { buildPatchFromChange } from '@/utils/patchMap';
import { useAuth } from '@/contexts/AuthContext';

interface TradingParametersProps {
  botId?: string;
  maxDailyTrades?: number | string;
  onMaxDailyTradesChange: (value: number) => void;
  precisionPercentage?: number | string;
  onPrecisionPercentageChange: (value: number) => void;
  onUpdate?: (values: any) => void;
}

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
}

const Tooltip: React.FC<TooltipProps> = ({ content, children }) => {
  return (
    <div className="group relative inline-block">
      {children}
      <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 w-96 hidden group-hover:block z-50">
        <div className="bg-gray-900 text-white text-sm rounded-lg p-4 shadow-xl">
          {content}
          <div className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 bg-gray-900 transform rotate-45"></div>
        </div>
      </div>
    </div>
  );
};

export const TradingParameters: React.FC<TradingParametersProps> = ({
  botId,
  maxDailyTrades,
  onMaxDailyTradesChange,
  precisionPercentage,
  onPrecisionPercentageChange,
  onUpdate
}) => {
  const { user } = useAuth();
  
  const [localMaxDailyTrades, setLocalMaxDailyTrades] = useState<number | string>(maxDailyTrades ?? '');
  const [localPrecisionPercentage, setLocalPrecisionPercentage] = useState<number | string>(precisionPercentage ?? '');

  useEffect(() => {
    if (maxDailyTrades !== undefined) setLocalMaxDailyTrades(maxDailyTrades);
  }, [maxDailyTrades]);

  useEffect(() => {
    if (precisionPercentage !== undefined) setLocalPrecisionPercentage(precisionPercentage);
  }, [precisionPercentage]);

  const handleMaxDailyTradesChange = async (value: number) => {
    setLocalMaxDailyTrades(value);
    onMaxDailyTradesChange(value);

    if (botId && user?.id) {
      await buildPatchFromChange(user.id, botId, 'max_daily_trades', value);
    }
  };

  const handlePrecisionPercentageChange = async (value: number) => {
    setLocalPrecisionPercentage(value);
    onPrecisionPercentageChange(value);

    if (botId && user?.id) {
      await buildPatchFromChange(user.id, botId, 'precision_percentage', value);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Trading Parameters</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Control trading frequency and signal quality</p>
          </div>
          <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
            <Settings className="h-6 w-6 text-blue-500" />
          </div>
        </div>

        <div className="space-y-6">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-500" />
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Max Daily Trades
              </label>
              <Tooltip content={
                <div className="space-y-2">
                  <p className="font-semibold">Maximum Daily Trades</p>
                  <p>Sets the maximum number of trades the bot can execute in a single day. This helps control trading activity and risk exposure.</p>
                  <ul className="list-disc list-inside space-y-1 mt-2">
                    <li>Lower values: More conservative, fewer opportunities</li>
                    <li>Higher values: More active trading, increased opportunity</li>
                    <li>0: Unlimited trades</li>
                  </ul>
                </div>
              }>
                <Info className="w-4 h-4 text-gray-400 cursor-help" />
              </Tooltip>
            </div>
            <span className="text-lg font-bold text-gray-900 dark:text-white">
              {localMaxDailyTrades === 0 ? 'Unlimited' : (localMaxDailyTrades !== '' ? localMaxDailyTrades : '')}
            </span>
          </div>

          <input
            type="range"
            min="0"
            max="100"
            step="1"
            value={localMaxDailyTrades === '' ? 0 : localMaxDailyTrades}
            onChange={(e) => handleMaxDailyTradesChange(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
          />

          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>0 (Unlimited)</span>
            <span>50</span>
            <span>100</span>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-500" />
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Trade Precision Filter
              </label>
              <Tooltip content={
                <div className="space-y-2">
                  <p className="font-semibold">Trade Precision Percentage</p>
                  <p>Sets the minimum confidence threshold for trade signals. Higher values mean stricter entry criteria.</p>
                  <ul className="list-disc list-inside space-y-1 mt-2">
                    <li>0%: Accept all signals (maximum trades)</li>
                    <li>50%: Balanced approach (recommended)</li>
                    <li>100%: Only perfect signals (very conservative)</li>
                  </ul>
                  <p className="mt-2 text-yellow-300">
                    ⚠️ Higher precision may reduce trading frequency
                  </p>
                </div>
              }>
                <Info className="w-4 h-4 text-gray-400 cursor-help" />
              </Tooltip>
            </div>
            <span className="text-lg font-bold text-gray-900 dark:text-white">
              {localPrecisionPercentage !== '' ? `${localPrecisionPercentage}%` : ''}
            </span>
          </div>

          <input
            type="range"
            min="0"
            max="100"
            step="1"
            value={localPrecisionPercentage === '' ? 0 : localPrecisionPercentage}
            onChange={(e) => handlePrecisionPercentageChange(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
          />

          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>0% (All)</span>
            <span>50%</span>
            <span>100% (Perfect)</span>
          </div>
        </div>

        <div className="bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-lg p-4 mt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-700 dark:text-gray-300">
              <p className="font-medium text-blue-700 dark:text-blue-300 mb-1">Trading Parameters Guide</p>
              <p>
                These parameters control how aggressively your bot trades and how selective it is with entry signals.
                Adjust based on your risk tolerance and trading strategy.
              </p>
            </div>
          </div>
        </div>
        </div>
      </div>

      <style>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          box-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
        }
        .slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: none;
          box-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
        }
        .slider::-webkit-slider-thumb:hover {
          background: #2563eb;
          box-shadow: 0 0 15px rgba(59, 130, 246, 0.8);
        }
        .slider::-moz-range-thumb:hover {
          background: #2563eb;
          box-shadow: 0 0 15px rgba(59, 130, 246, 0.8);
        }
      `}</style>
    </motion.div>
  );
};